import React, { useState } from 'react';
import { Menu, MenuItemLink, MenuProps } from 'react-admin';
import { Collapse, ListItemButton, ListItemText } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import BookOnlineRounded from '@mui/icons-material/BookOnlineRounded';
import PeopleIcon from '@mui/icons-material/People';
import TimelineIcon from '@mui/icons-material/Timeline';
import AppRegistration from '@mui/icons-material/AppRegistration';
import SecurityIcon from '@mui/icons-material/SecurityOutlined';
import StoreIcon from '@mui/icons-material/Store';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import GroupsIcon from '@mui/icons-material/Groups';
import TableChartIcon from '@mui/icons-material/TableChart';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CodeIcon from '@mui/icons-material/Code';
import ChatIcon from '@mui/icons-material/Chat';
import FeedbackIcon from '@mui/icons-material/Feedback';
import ScienceIcon from '@mui/icons-material/Science';
import InsightsIcon from '@mui/icons-material/Insights';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HandshakeIcon from '@mui/icons-material/Handshake';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import PercentIcon from '@mui/icons-material/Percent';
import PaymentsIcon from '@mui/icons-material/Payments';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StorageIcon from '@mui/icons-material/Storage';
import HomeIcon from '@mui/icons-material/Home';

// Groupe de menu repliable (parent cliquable).
const MenuGroup: React.FC<{ label: string; defaultOpen?: boolean; children: React.ReactNode }> = ({
	label,
	defaultOpen = true,
	children,
}) => {
	const [open, setOpen] = useState(defaultOpen);
	return (
		<>
			<ListItemButton onClick={() => setOpen(!open)} sx={{ py: 0.5, mt: 1 }}>
				<ListItemText
					primary={label}
					primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 700 }}
				/>
				{open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
			</ListItemButton>
			<Collapse in={open} timeout="auto" unmountOnExit>
				{children}
			</Collapse>
		</>
	);
};

const CustomMenu: React.FC<MenuProps> = (props) => (
	<Menu {...props}>
		{/* Hors de tout groupe : la home (`/`) est une CustomRoute, pas une Resource, donc
		    react-admin ne lui crée aucune entrée. Sans ce lien, revenir à l'accueil
		    demandait de retaper l'URL. */}
		<MenuItemLink to="/" primaryText="Home" leftIcon={<HomeIcon />} />

		<MenuGroup label="Ressource Management" defaultOpen>
			<MenuItemLink to="/projects" primaryText="Projects" leftIcon={<InboxIcon />} />
			<MenuItemLink to="/connector-accounts" primaryText="Connector Accounts" leftIcon={<AppRegistration />} />
			<MenuItemLink to="/billing-accounts" primaryText="Billing Accounts" leftIcon={<StoreIcon />} />
			<MenuItemLink to="/users" primaryText="Users" leftIcon={<PeopleIcon />} />
			<MenuItemLink to="/llm-instances" primaryText="LLM Instances" leftIcon={<SmartToyIcon />} />
			<MenuItemLink to="/datawarehouse" primaryText="Datawarehouse" leftIcon={<StorageIcon />} />
			<MenuItemLink to="/activities" primaryText="Activités" leftIcon={<TimelineIcon />} />
			<MenuItemLink to="/logs" primaryText="Logs" leftIcon={<BookOnlineRounded />} />
			<MenuItemLink to="/credential-requests" primaryText="Credential Requests" leftIcon={<VpnKeyIcon />} />
		</MenuGroup>

		{/* Pricing v2 : les droits se posent par produit sur la fiche compte (onglet Products).
		    Plans / Features / Overrides n'ont plus d'effet — écrans conservés, retirés du menu. */}
		<MenuGroup label="Facturation" defaultOpen>
			<MenuItemLink to="/products" primaryText="Produits" leftIcon={<LocalOfferIcon />} />
			<MenuItemLink to="/subscriptions" primaryText="Abonnements" leftIcon={<SubscriptionsIcon />} />
			<MenuItemLink
				to="/billing-alerts"
				primaryText="Alertes facturation"
				leftIcon={<ReportProblemIcon />}
			/>
		</MenuGroup>

		<MenuGroup label="Partenaires" defaultOpen>
			{/* ⚠️ En TÊTE du groupe : c'est une file d'attente, et la seule entrée du menu qui
			    demande une action de notre part. Une demande qui dort faute d'avoir été vue est
			    un partenaire qu'on perd — rien d'autre ne la signale. */}
			<MenuItemLink to="/partner-signup-requests" primaryText="Demandes d'accès" leftIcon={<HowToRegIcon />} />
			<MenuItemLink to="/partners" primaryText="Partenaires" leftIcon={<HandshakeIcon />} />
			<MenuItemLink to="/opportunities" primaryText="Opportunités" leftIcon={<ContactPhoneIcon />} />
			{/* Contenu servi à l'espace partenaire (ui-partners → /cas-d-usage). Les
			    thèmes sont une ressource à part : ils sont partagés entre les fiches. */}
			<MenuItemLink to="/use-cases" primaryText="Cas d'usage" leftIcon={<LightbulbIcon />} />
			<MenuItemLink to="/use-case-themes" primaryText="Thèmes" leftIcon={<LocalOfferIcon />} />
			<MenuItemLink to="/commission-rates" primaryText="Grille de commission" leftIcon={<PercentIcon />} />
			<MenuItemLink to="/payouts" primaryText="Déblocages" leftIcon={<PaymentsIcon />} />
		</MenuGroup>

		<MenuGroup label="Parameters" defaultOpen={false}>
			<MenuItemLink to="/connectors" primaryText="Connectors" leftIcon={<AppRegistration />} />
			<MenuItemLink to="/2fa" primaryText="2FA" leftIcon={<SecurityIcon />} />
		</MenuGroup>

		<MenuGroup label="MCP Internal" defaultOpen={false}>
			<MenuItemLink to="/mcp-members" primaryText="Membres" leftIcon={<GroupsIcon />} />
		</MenuGroup>

		<MenuGroup label="MCP Public (Quanti AI)" defaultOpen={false}>
			<MenuItemLink to="/ai-enrichments" primaryText="Enrichissements" leftIcon={<TableChartIcon />} />
			<MenuItemLink to="/ai-use-cases" primaryText="Use Cases" leftIcon={<AutoAwesomeIcon />} />
			<MenuItemLink to="/ai-conversations" primaryText="Conversations" leftIcon={<ChatIcon />} />
			<MenuItemLink to="/ai-feedback" primaryText="Feedbacks" leftIcon={<FeedbackIcon />} />
			<MenuItemLink to="/ai-analyses" primaryText="Analyses IA" leftIcon={<InsightsIcon />} />
			<MenuItemLink to="/ai-semantic-codes" primaryText="Semantic Codes" leftIcon={<CodeIcon />} />
			<MenuItemLink to="/mcp-test" primaryText="Test Tools" leftIcon={<ScienceIcon />} />
		</MenuGroup>

		<MenuGroup label="Agents IA" defaultOpen={false}>
			<MenuItemLink to="/agents" primaryText="Organisation & agents" leftIcon={<SmartToyIcon />} />
		</MenuGroup>
	</Menu>
);

export default CustomMenu;
